/* Fetch the current cart's real Shopify rates. This optional check never blocks checkout. */
class GeethasShippingEstimate extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.input = this.querySelector('[data-postcode]');
    this.province = this.querySelector('[data-province]');
    this.button = this.querySelector('[data-estimate]');
    this.result = this.querySelector('[data-result]');
    if (!this.input || !this.button || !this.result) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.button.addEventListener('click', () => this.estimate(), options);
    for (const input of [this.input, this.province].filter(Boolean)) {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') { event.preventDefault(); this.estimate(); }
      }, options);
      input.addEventListener('input', () => this.reset(), options);
    }
    // Cart sections can be replaced after a quantity or market change.
    for (const event of ['shopify:cart:lines-update', 'shopify:cart:discount-update', 'quantity-selector:update', 'cart-section:restored']) {
      document.addEventListener(event, () => this.reset(), options);
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.request?.abort();
    this.abort = null;
  }

  reset() {
    this.request?.abort();
    this.request = null;
    this.input?.removeAttribute('aria-invalid');
    this.province?.removeAttribute('aria-invalid');
    this.result?.replaceChildren();
    if (this.button) this.button.disabled = false;
    this.removeAttribute('aria-busy');
  }

  async estimate() {
    if (!this.button || this.button.disabled) return;
    const postcode = this.input.value.trim();
    const province = this.province?.value.trim() || '';
    const postcodeValid = postcode.length > 0 || this.dataset.countryCode === 'AE';
    if (!postcodeValid || (this.province && !province)) {
      const invalidInput = !postcodeValid ? this.input : this.province;
      invalidInput.setAttribute('aria-invalid', 'true');
      this.result.textContent = this.dataset.invalid;
      invalidInput.focus();
      return;
    }
    this.input.removeAttribute('aria-invalid');
    this.province?.removeAttribute('aria-invalid');
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const timeout = setTimeout(() => request.abort(), 12000);
    this.button.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.result.textContent = this.dataset.loading;
    const root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
    const query = new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': this.dataset.country,
    });
    if (province) query.set('shipping_address[province]', province);
    const currentRequest = () => this.isConnected && this.request === request && !request.signal.aborted;
    const cartMatches = (cart) => cart.currency === this.dataset.currency &&
      Number(cart.total_price) === Number(this.dataset.total) && cart.item_count > 0;
    const fingerprint = (cart) => JSON.stringify((cart.items || []).map((item) => [item.key, item.quantity, item.final_line_price]));
    const readCart = async () => {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: request.signal });
      if (!response.ok) throw new Error('cart');
      const cart = await response.json();
      if (!cartMatches(cart)) throw new Error('cart changed');
      return cart;
    };
    try {
      const initialCart = await readCart();
      const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${query}`, {
        method: 'POST', credentials: 'same-origin', signal: request.signal,
      });
      if (!prepared.ok) throw new Error('shipping');
      let rates = null;
      for (let attempt = 0; attempt < 10; attempt += 1) {
        const response = await fetch(`${root}cart/async_shipping_rates.json?${query}`, {
          credentials: 'same-origin', cache: 'no-store', signal: request.signal,
        });
        if (!response.ok) throw new Error('shipping');
        const data = await response.json();
        if (Array.isArray(data?.shipping_rates)) { rates = data.shipping_rates; break; }
        await new Promise((resolve) => setTimeout(resolve, 600));
        if (!currentRequest()) return;
      }
      if (!rates) throw new Error('shipping');
      const cart = await readCart();
      if (fingerprint(cart) !== fingerprint(initialCart)) throw new Error('cart changed');
      if (!currentRequest()) return;
      this.result.replaceChildren();
      if (!rates.length) { this.result.textContent = this.dataset.empty; return; }
      const list = document.createElement('ul');
      const formatter = new Intl.NumberFormat(this.dataset.locale || 'en', {
        style: 'currency', currency: cart.currency, currencyDisplay: 'code',
      });
      for (const rate of rates.sort((a, b) => Number(a.price) - Number(b.price))) {
        const price = Number(rate.price);
        const currency = rate.currency || cart.currency;
        if (rate.price == null || rate.price === '' || !Number.isFinite(price) || price < 0 || currency !== cart.currency) {
          throw new Error('currency');
        }
        const line = document.createElement('li');
        line.textContent = `${rate.presentment_name || rate.name}: ${price === 0 ? this.dataset.free : formatter.format(price)}`;
        list.appendChild(line);
      }
      this.result.appendChild(list);
    } catch (error) {
      if (this.isConnected && this.request === request) this.result.textContent = this.dataset.error;
    } finally {
      clearTimeout(timeout);
      if (this.isConnected && this.request === request) {
        this.button.disabled = false;
        this.removeAttribute('aria-busy');
        this.request = null;
      }
    }
  }
}
if (!customElements.get('geethas-shipping-estimate')) customElements.define('geethas-shipping-estimate', GeethasShippingEstimate);
